import { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Button,
  Divider,
  Collapse,
  IconButton,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  TextField,
  InputAdornment,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import BugReportIcon from '@mui/icons-material/BugReport';
import SearchIcon from '@mui/icons-material/Search';
import useDevRole from '../../hooks/useDevRole';
import useParameters from '../../hooks/useParameters';
import { db } from '../../lib/firebase';

function DevRolePanel() {
  if (import.meta.env.MODE !== 'development') {
    return null;
  }
  const {
    isEmulatingRoles,
    isClearingSucursales,
    emulatedRoles,
    realRoles,
    toggleRole,
    clearEmulation,
    applyPreset,
    toggleSucursalEmulation,
  } = useDevRole();

  const { parametrosDB } = useParameters();

  const [expanded, setExpanded] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState('');
  const [perfiles, setPerfiles] = useState([]);
  const [loadingPerfiles, setLoadingPerfiles] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  const isEmulating = isEmulatingRoles || isClearingSucursales;

  const roleDescriptions = useMemo(() => {
    if (!parametrosDB?.funciones) return {};

    const map = {};
    parametrosDB.funciones.forEach((func) => {
      map[func.value] = func.text;
    });
    return map;
  }, [parametrosDB]);

  const getRoleLabel = (role) => {
    const roleNumber = role.match(/f(\d+)f/);
    const fullDescription = roleDescriptions[role];
    let description = '';
    if (fullDescription) {
      const justDescription = fullDescription.match(/^\d+\. (.+)$/);
      description = justDescription ? justDescription[1] : fullDescription;
    } else {
      description = 'Sin descripción';
    }
    return roleNumber ? `${roleNumber[1]}. ${description}` : `${role}. ${description}`;
  };

  const sortedRoles = useMemo(() => {
    return [...realRoles].sort((a, b) => {
      const numA = parseInt(a.match(/f(\d+)f/)?.[1] || '0', 10);
      const numB = parseInt(b.match(/f(\d+)f/)?.[1] || '0', 10);
      return numA - numB;
    });
  }, [realRoles]);

  const filteredRoles = useMemo(() => {
    if (!searchQuery.trim()) return sortedRoles;

    const query = searchQuery.toLowerCase().trim();

    return sortedRoles.filter((role) => {
      // Extract number from role (e.g., "f1f" -> "1")
      const match = role.match(/f(\d+)f/);
      const roleNumber = match ? match[1] : '';

      const description = roleDescriptions[role] || '';

      // Search by:
      // 1. Role number (with or without 'f')
      // 2. Role description
      return roleNumber.includes(query) || role.includes(query) || description.toLowerCase().includes(query);
    });
  }, [sortedRoles, searchQuery, roleDescriptions]);

  // Lazy load perfiles only when panel is expanded
  useEffect(() => {
    if (!expanded || perfiles.length > 0) return;

    const getPerfiles = async () => {
      try {
        setLoadingPerfiles(true);
        const perfilesLoad = [];
        const response = await db.collection('perfiles').get();
        response.docs.forEach((doc) => {
          perfilesLoad.push({ ...doc.data() });
        });
        setPerfiles(perfilesLoad);
      } catch (e) {
        console.error('Error loading perfiles:', e);
      } finally {
        setLoadingPerfiles(false);
      }
    };

    getPerfiles();
  }, [expanded, perfiles.length]);

  const handlePresetChange = (event) => {
    const presetId = event.target.value;
    setSelectedPreset(presetId);
    const perfil = perfiles.find((p) => p.id === presetId);
    if (perfil && perfil.role) {
      // Parse roles from the role string (e.g., "f1ff2f" -> ["f1f", "f2f"])
      const presetRoles = perfil.role.match(/f\d+f/g) || [];
      applyPreset(presetRoles);
    }
  };

  const handleReset = () => {
    setSelectedPreset('');
    clearEmulation();
  };

  const isRoleEnabled = (role) => {
    if (!isEmulatingRoles) return true;
    return emulatedRoles.includes(role);
  };

  return (
    <Box
      sx={(theme) => ({
        backgroundColor: 'rgba(255, 193, 7, 0.08)',
        borderLeft: `3px solid ${theme.palette.warning.light}`,
      })}
    >
      <Box
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1, cursor: 'pointer' }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BugReportIcon sx={{ color: 'warning.light', fontSize: 20 }} />
          <Typography variant="subtitle2" sx={{ color: 'warning.light', fontWeight: 600 }}>
            Dev: Emular Roles
          </Typography>
          {isEmulating && (
            <Chip
              label="EMULANDO"
              size="small"
              sx={(theme) => ({
                backgroundColor: theme.palette.warning.main,
                color: '#000',
                fontWeight: 700,
                fontSize: '0.65rem',
                height: 20,
              })}
            />
          )}
        </Box>
        <IconButton size="small" sx={{ color: 'warning.light' }}>
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ px: 2, pb: 2 }}>
          {isEmulating && (
            <Alert severity="warning" sx={{ mb: 2, py: 0.5 }}>
              <Typography variant="caption">
                Los roles emulados solo afectan la UI. El backend ve tus roles reales.
              </Typography>
            </Alert>
          )}

          {/* Preset selector */}
          <FormControl fullWidth size="small" sx={{ mb: 2 }} disabled={loadingPerfiles}>
            <InputLabel>Aplicar Perfil</InputLabel>
            <Select
              value={selectedPreset}
              label="Aplicar Perfil"
              onChange={handlePresetChange}
              startAdornment={loadingPerfiles && <CircularProgress size={16} sx={{ mr: 1 }} />}
            >
              {perfiles.map((perfil) => (
                <MenuItem key={perfil.id} value={perfil.id}>
                  {perfil.text}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Divider sx={{ my: 1 }} />

          <TextField
            fullWidth
            size="small"
            placeholder="Buscar por número o nombre..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 1 }}
          />

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            {searchQuery ? `${filteredRoles.length} de ${realRoles.length} roles` : `Tus roles (${realRoles.length})`}
          </Typography>
          <Box
            sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 0, maxHeight: 200, overflowY: 'auto' }}
          >
            {filteredRoles.length > 0 ? (
              filteredRoles.map((role) => (
                <FormControlLabel
                  key={role}
                  control={
                    <Checkbox
                      size="small"
                      checked={isRoleEnabled(role)}
                      onChange={() => toggleRole(role)}
                      sx={{ py: 0.25, '&.Mui-checked': { color: 'warning.main' } }}
                    />
                  }
                  label={
                    <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                      {getRoleLabel(role)}
                    </Typography>
                  }
                  sx={{ m: 0 }}
                />
              ))
            ) : (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ gridColumn: '1 / -1', textAlign: 'center', py: 2 }}
              >
                No se encontraron roles
              </Typography>
            )}
          </Box>

          {isEmulatingRoles && (
            <>
              <Divider sx={{ my: 1 }} />
              <Typography variant="caption" color="text.secondary">
                Roles activos: {emulatedRoles.length} / {realRoles.length}
              </Typography>
            </>
          )}

          <Box sx={{ mt: 2 }}>
            <Button
              fullWidth
              size="small"
              variant={isEmulating ? 'contained' : 'outlined'}
              color={isEmulating ? 'warning' : 'inherit'}
              onClick={handleReset}
              disabled={!isEmulating}
            >
              Restaurar Roles Reales
            </Button>
          </Box>
          <Divider sx={{ my: 1 }} />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={isClearingSucursales}
                onChange={toggleSucursalEmulation}
                sx={{ py: 0.25, '&.Mui-checked': { color: 'warning.main' } }}
              />
            }
            label={<Typography variant="caption">Quitar sucursales</Typography>}
            sx={{ m: 0 }}
          />
        </Box>
      </Collapse>
    </Box>
  );
}

export default DevRolePanel;
